import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _uniqueId from "lodash/uniqueId";
import _uniqBy from "lodash/uniqBy";
import _union from "lodash/union";
import _sortBy2 from "lodash/sortBy";
import _orderBy from "lodash/orderBy";
import _isString from "lodash/isString";
import _isMatchWith from "lodash/isMatchWith";
import _isMatch from "lodash/isMatch";
import _isEqual from "lodash/isEqual";
import _isArray from "lodash/isArray";
import _escapeRegExp from "lodash/escapeRegExp";
/* eslint-disable @typescript-eslint/no-explicit-any */
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15, _descriptor16, _descriptor17, _descriptor18, _descriptor19, _descriptor20, _descriptor21, _descriptor22, _descriptor23, _descriptor24, _descriptor25;
import { action, computed, observable } from 'mobx';
import safelyParseJSON from '@kentik/ui-shared/util/safelyParseJson';
import api from 'core/util/api';
import collatePromises from 'core/util/collatePromises';
import { processLargeArrayAsync } from 'core/util';
import GenericModel, { Model } from 'core/model/Model';

/**
 * The set of keys that make up the type underlying a targeted model (attributes and getters)
 */

/**
 * The acceptable sort states for a given Collection
 */

/**
 * The acceptable grouping states for a given Collection.
 */

/**
 * The constructor of the Model owned by this Collection.
 */

/**
 * Like regular Model JSON except with additional fields that come from the API. Note that all
 * API fields are marked as optional - this means that you can also pass in regular objects and
 * everything will still function!
 */

/**
 * Every form of acceptable Model input. This includes:
 * - the Model itself,
 * - ModelConstructorArgs - just the JSON required to construct the object.
 * - Model Type (i.e. Model.attributes)
 *   - technically a superset of ModelConstructorArgs but TS doesn't automatically understand that!
 *
 * Note that sometimes TS errors can be confusing here when it tests against the Model.
 * Consider asserting InferModelConstructorArgs<MOD>.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export let Collection = (_dec = observable.shallow, _dec2 = observable.ref, _class = class Collection {
  get selectedArray() {
    if (!this.selected) {
      return [];
    }
    return Array.isArray(this.selected) ? this.selected : [this.selected];
  }

  /**
   * Inverse of `this.selectedArray()` which guarantees array selection, this
   * instead guarantees a single selection - even if multiple items are selected!
   *
   * Obviously "selecting" only the first entry in an array could result in weird
   * unexpected behavior but it's likely safer than passing an array when an object is expected.
   */
  get singleSelection() {
    if (!this.selected) {
      return null;
    }
    if (Array.isArray(this.selected)) {
      return this.selected.length ? this.selected[0] : null;
    }
    return this.selected;
  }
  constructor(data = [], _options = {}) {
    _initializerDefineProperty(this, "requestStatus", _descriptor, this);
    this.models = observable.array([], {
      deep: false
    });
    this.modelById = {};
    /**
     * list of fields that, when used to sort, will trigger a server-side data load
     *
     * Ideally this would be protected and readonly but we do set it externally in some cases
     */
    this.serverSortableFields = [];
    // list of fields that, when used to sort, will need ui-driven logic to create a sorting filter for API
    this.uiSortLogicFields = [];
    _initializerDefineProperty(this, "error", _descriptor2, this);
    this.id = _uniqueId('c_');
    _initializerDefineProperty(this, "filterState", _descriptor3, this);
    _initializerDefineProperty(this, "activePresetFilter", _descriptor4, this);
    _initializerDefineProperty(this, "discreteFilters", _descriptor5, this);
    _initializerDefineProperty(this, "sortState", _descriptor6, this);
    _initializerDefineProperty(this, "groupBy", _descriptor7, this);
    _initializerDefineProperty(this, "selected", _descriptor8, this);
    _initializerDefineProperty(this, "hasFetched", _descriptor9, this);
    _initializerDefineProperty(this, "original", _descriptor0, this);
    _initializerDefineProperty(this, "lastUpdated", _descriptor1, this);
    _initializerDefineProperty(this, "lastFetched", _descriptor10, this);
    _initializerDefineProperty(this, "useAsyncAdd", _descriptor11, this);
    this.fetchPromise = null;
    _initializerDefineProperty(this, "totalCount", _descriptor12, this);
    _initializerDefineProperty(this, "hasMoreResults", _descriptor13, this);
    this.threeWaySort = void 0;
    this.store = void 0;
    // eslint-disable-next-line no-use-before-define,@typescript-eslint/no-explicit-any
    this.parent = void 0;
    this.getSortValue = void 0;
    _initializerDefineProperty(this, "fetchCount", _descriptor14, this);
    this.lastData = void 0;
    _initializerDefineProperty(this, "cachedResults", _descriptor15, this);
    this.hashModelsById = () => {
      const modelHash = {};
      this.get().forEach(model => {
        if (model && model.id) {
          modelHash[model.id] = model;
        }
      });
      this.modelById = modelHash;
    };
    /**
     * Creates a new model instance with the given attributes
     */
    this.build = (attributes, options = {}) => {
      const {
        select = false,
        deserialize = true
      } = options;
      const ModelClass = this.model;
      const model = new ModelClass();
      model.store = this.store;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      model.collection = this;
      model.set(deserialize ? model.deserialize(attributes) : attributes);
      if (select) {
        this.selected = model;
      }
      return model;
    };
    this.generateSelectOptions = ({
      valueKey = 'id',
      labelKey,
      sortBy,
      unfiltered = false
    }) => {
      const models = unfiltered ? this.unfiltered : this.models;
      const options = models.map(model => ({
        value: model.get(valueKey) || model[valueKey],
        label: labelKey ? model.get(labelKey) : model.get(valueKey),
        ...model.get()
      }));
      if (sortBy) {
        return _orderBy(options, sortBy);
      }
      return options;
    };
    _initializerDefineProperty(this, "unselectAll", _descriptor16, this);
    _initializerDefineProperty(this, "select", _descriptor17, this);
    _initializerDefineProperty(this, "clearSelection", _descriptor18, this);
    _initializerDefineProperty(this, "setLastUpdated", _descriptor19, this);
    _initializerDefineProperty(this, "setLastFetched", _descriptor20, this);
    _initializerDefineProperty(this, "setRequestStatus", _descriptor21, this);
    /**
     * Adds one or more models to the collection. Accepts an array of models/raw objects or a simple model/object.
     * Returns the added models.
     *
     * DO NOT CALL INSIDE A LOOP as this is somewhat expensive. Instead, prefer constructing complete collection and passing that in.
     *
     * @param data technically you can pass in single objects as well but object[] is better for type hinting and functionally identical otherwise
     */
    _initializerDefineProperty(this, "add", _descriptor22, this);
    _initializerDefineProperty(this, "remove", _descriptor23, this);
    /**
     * Sets the model(s) in the collection. Accepts an array of models/raw objects or a simple model/object.
     */
    _initializerDefineProperty(this, "set", _descriptor24, this);
    /**
     * Creates a model object of the type the Collection instance uses with the provided data
     * optionally selecting it (this is the default behavior). The resultant model is not added
     * to the collection!
     */
    _initializerDefineProperty(this, "forge", _descriptor25, this);
    this.getServerSortBy = () => {
      const canServerSort = this.sortFieldIsServerSortable || typeof this.groupBy === 'string' && this.serverSortableFields.includes(this.groupBy);
      let sortBy;
      if (canServerSort) {
        // first sorting is done by grouping in terms of api search
        sortBy = typeof this.groupBy === 'string' && this.groupBy || '';
        if (this.sortState.field && this.sortFieldIsServerSortable) {
          // second sorting is done by actual sort on a specific field - comma separated
          // format: groupBy,sortBy:order
          sortBy = sortBy ? `${sortBy},${this.sortState.field}:${this.sortState.direction}` : `${this.sortState.field}:${this.sortState.direction}`;
        }
      }
      return sortBy;
    };
    this.loadMoreItems = ({
      startIndex,
      reset = false,
      force = false
    }) => {
      const sortBy = this.getServerSortBy();
      const isUnfiltered = this.size === this.unfilteredSize;
      // If there's a collection filter, use the unfiltered size as our offset
      const offset = isUnfiltered ? startIndex : this.unfilteredSize;
      const hadMoreResults = this.hasMoreResults;
      if (reset) {
        this.reset();
      }

      // this.totalCount represents total possible results for a query.
      // If our offset is greater/equal to that, no need to kick off another fetch.
      if (this.totalCount != null && offset && offset >= this.totalCount && !hadMoreResults) {
        return Promise.resolve(null);
      }
      return this.fetch({
        pagedFetch: !!startIndex,
        data: {
          force,
          offset,
          sortBy: sortBy?.toString()
        }
      });
    };
    const {
      activePresetFilter = this.defaultPresetFilter,
      sortState = this.defaultSortState,
      discreteFilters = this.defaultDiscreteFilters,
      groupBy: currGroupBy = this.defaultGroupBy,
      getSortValue,
      hasFetched = false,
      models: _models,
      threeWaySort = false,
      store = null
    } = _options;
    if (store) {
      this.store = store;
    }
    this.parent = _options.parent || undefined;

    // allows for custom getSortValue in generic Collection usage
    if (typeof getSortValue === 'function') {
      this.getSortValue = getSortValue;
    }

    // manual bind for converted arrow functions. Allows subclasses to override but keeps arrow function "this" behavior.
    this.processData = this.processData.bind(this);
    this.reset = this.reset.bind(this);
    this.activePresetFilter = activePresetFilter;
    this.threeWaySort = threeWaySort;
    this.sortState = sortState;
    this.discreteFilters = discreteFilters;
    this.groupBy = currGroupBy;
    if (_models) {
      this.models.replace(_models);
      this.hashModelsById();
    } else {
      this.set(data);
    }

    // Most common use case for this would be when a collection is initialized with data upfront.
    // When a collection is initialized with data and a PresetFilterButton group is applied to it,
    // you may encounter a situation where the filter buttons all become disabled when filtering by a property
    // that yields 0 results. Setting this option will prevent that.
    this.hasFetched = hasFetched;
  }
  get urlPaths() {
    return undefined;
  }
  get fetchMethod() {
    return 'get';
  }
  get queuedFetchKey() {
    return null;
  }
  get defaultSortState() {
    return {};
  }
  get defaultDiscreteFilters() {
    return [];
  }
  get baseSort() {
    return null;
  }
  get secondarySort() {
    return null;
  }
  get defaultGroupBy() {
    return null;
  }
  get defaultPresetFilter() {
    return this.presetFilters && this.presetFilters.find(filter => filter.default) || undefined;
  }
  get presetFilters() {
    return false;
  }

  /**
   * Appears entirely unused? Should probably be deleted but I'm leaving it in for now.
   * @private
   * @deprecated should be removed if this continues to go unused
   */
  get discreteFiltersTypes() {
    return this.discreteFilters.map(filter => filter.type);
  }
  get minFetchInterval() {
    return 300000;
  }

  /**
   * Returns the URL where the model's resource would be located on the server.
   */
  get url() {
    return undefined;
  }

  /**
   * Specifies the model class for that collection
   */

  /**
   * Returns this Collection's Models as raw JSON.
   */
  toJS() {
    return this.models.map(model => model.toJS());
  }

  /**
   * Describes the behavior of what a server side sort means, can be overridden at the collection level
   */
  serverSortFn() {
    return this.loadMoreItems({
      startIndex: 0,
      force: true,
      reset: true
    });
  }
  /**
   * Questions whether the request exists and matches a certain label
   * If no label are passed it will check if any requests are active.
   */
  isRequestActive(label) {
    if (this.requestStatus === 'awaitingRequest') {
      return true;
    }
    if (!label) {
      return this.requestStatus !== null;
    }
    return !!this.requestStatus && this.requestStatus === label;
  }

  /**
   * Whether the collection is empty
   */
  isEmpty() {
    return this.models.length === 0;
  }

  /**
   * Get a resource at a given position
   */
  at(index) {
    if (index < 0 || index >= this.size || !this.models[index]) {
      console.warn(`No model found at index ${index}`);
      return false;
    }
    return this.models[index];
  }

  /**
   * Like `this.at()` except safer since it returns null when no result
   */
  att(index) {
    return this.at(index) || null;
  }
  /**
   * Get a resource with the given id or uuid or if no id is provided, will return all the models
   */
  get(id) {
    if (id !== undefined) {
      return this.modelById[id];
    }
    const models = this.original || this.models;
    return models.slice();
  }
  [Symbol.iterator]() {
    return this.models[Symbol.iterator]();
  }
  each(iterator) {
    this.models.forEach(iterator);
  }
  map(iterator) {
    return this.models.map(iterator);
  }
  some(iterator) {
    return this.models.some(iterator);
  }
  every(iterator) {
    return this.models.every(iterator);
  }
  reduce(iterator, initialValue) {
    return this.models.reduce(iterator, initialValue);
  }
  slice(start, end) {
    return this.models.slice(start, end);
  }

  /**
   * Standard JS array.find()
   */

  /**
   * Lodash predicate search
   */

  /**
   * Finds an element with the given matcher
   */
  find(query, customizer) {
    // standard JS array.find()
    if (typeof query === 'function') {
      return this.models.find(query);
    }
    // lodash predicate find
    return this.models.find(model => customizer ? _isMatchWith(model.attributes, query, customizer) : _isMatch(model.attributes, query));
  }
  group(value) {
    const loadMore = this.groupBy !== value && (value ? this.serverSortableFields.includes(value) : this.sortFieldIsServerSortable);
    this.groupBy = value;

    // if grouping by a new column that's server sortable, we also need to re-fetch
    if (loadMore) {
      this.loadMoreItems({
        startIndex: 0,
        force: true,
        reset: true
      });
    }
  }
  get isTreeGroupBy() {
    return false;
  }
  get groupedData() {
    if (!this.groupBy) {
      return {};
    }
    const groups = {};
    this.models.forEach(model => {
      // add ability to set this.groupBy to object. Expecting type(string) and groupByFn(function) properties.
      const group = typeof this.groupBy === 'object' ? this.groupBy?.groupByFn(model) : model[this.groupBy] ?? model.get(this.groupBy);
      if (Array.isArray(group)) {
        if (group.length > 0) {
          // add model to multiple groups (for labels, etc.)
          group.forEach(g => {
            groups[g] ??= [];
            groups[g].push(model);
          });
        } else {
          groups.None ??= [];
          groups.None.push(model);
        }
      } else {
        groups[group] ??= [];
        groups[group].push(model);
      }
    });
    return groups;
  }
  get sortedGroupKeys() {
    if (!this.groupBy) {
      return [];
    }
    const groupedModels = this.groupedData;

    // This assumed ALL the models are sorted, then this sorts the group indexes based on their current order in the whole collection
    return _sortBy2(Object.keys(groupedModels), groupKey => this.models.indexOf(groupedModels[groupKey][0]));
  }
  move(oldIndex, newIndex) {
    this.models.splice(newIndex, 0, this.models.splice(oldIndex, 1)[0]);
  }
  sort(field, direction) {
    let sortField = this.sortState.field;
    let sortDir = this.sortState.direction;

    // just clear the sort if the user clicked the same field while already sorted descending or sorted by the default direction
    if (this.threeWaySort && sortField === field && sortDir && (this.defaultSortState.direction && sortDir !== this.defaultSortState.direction || !this.defaultSortState.direction && sortDir === 'desc')) {
      this.clearSort();
      return;
    }

    // If a field was explicitly passed, use it
    if (field !== undefined) {
      sortField = field;
      sortDir = direction || this.defaultSortState.direction || 'asc';

      // If the field passed was the same as current, toggle the direction (or turn off)
      if (field === this.sortState.field && !direction) {
        sortDir = this.sortState.direction === 'asc' ? 'desc' : 'asc';
      }
    }
    if (sortField) {
      if (this.sortState.field !== sortField || this.sortState.direction !== sortDir) {
        this.sortState = {
          field: sortField,
          direction: sortDir
        };
      }

      // no need to sort locally if we already sorted on the server
      if (field === undefined && this.sortFieldIsServerSortable) {
        return;
      }
      this.original = this.get();
      const iteratees = [];
      const orders = [];
      let baseSortFn;
      let sortFn;
      const {
        getSortValue
      } = this;
      if (getSortValue) {
        baseSortFn = model => getSortValue(this.baseSort?.field, model);
        sortFn = model => getSortValue(sortField, model);
      } else {
        baseSortFn = model => model.getSortValue(this.baseSort?.field);
        sortFn = model => model.getSortValue(sortField);
      }
      if (this.baseSort) {
        iteratees.push(baseSortFn);
        orders.push(this.baseSort.direction || 'asc');
      }
      iteratees.push(sortFn);
      if (sortDir) {
        orders.push(sortDir);
      }
      if (this.secondarySort) {
        iteratees.push(model => model.getSortValue(this.secondarySort?.field));
        orders.push(this.secondarySort.direction || 'asc');
      }
      this.models.replace(_orderBy(this.models, iteratees, orders));
    }
  }
  serverSort(field, direction) {
    let sortField = this.sortState.field;
    let sortDir = this.sortState.direction;

    // just clear the sort if the user clicked the same field while already sorted descending or sorted by the default direction
    if (this.threeWaySort && sortField === field && sortDir && (this.defaultSortState.direction && sortDir !== this.defaultSortState.direction || !this.defaultSortState.direction && sortDir === 'desc')) {
      this.clearSort(true);
      return;
    }

    // If a field was explicitly passed, use it
    if (field !== undefined) {
      sortField = field;
      sortDir = direction || this.defaultSortState.direction || 'asc';

      // If the field passed was the same as current, toggle the direction (or turn off)
      if (field === this.sortState.field && !direction) {
        sortDir = this.sortState.direction === 'asc' ? 'desc' : 'asc';
      }
    }
    if (sortField) {
      if (this.sortState.field !== sortField || this.sortState.direction !== sortDir) {
        this.sortState = {
          field: sortField,
          direction: sortDir
        };
      }
    }
    if (this.serverSortFn) {
      this.serverSortFn();
    }
  }

  /**
   * Sets the sort state without forcing a sort. Useful to do prior to fetching.
   */
  setSortState(field, direction = 'asc') {
    this.sortState = {
      field,
      direction
    };
  }
  clearSort(serverSort) {
    this.sortState = {};
    if (this.threeWaySort && !serverSort && this.original) {
      this.set(this.original);
    } else if (serverSort) {
      this.serverSort();
    } else {
      this.sort();
    }
  }

  /**
   * Filter models, finishing with a sort. DO NOT CALL IN A LOOP.
   */
  filter(query, options = {}) {
    const {
      immutable = false,
      idPath = 'id',
      customFilter,
      guardForDuplicates
    } = options;
    if (immutable) {
      if (!query) {
        return this.get();
      }
      return this.get().filter(query);
    }
    if (query !== undefined) {
      this.filterState = query;
    }
    this.original = this.get();
    if (customFilter) {
      this.models.replace(this.original.filter(customFilter));
    } else if (this.filterState) {
      if (_isString(this.filterState)) {
        const terms = this.matchAllSearchTerms ? this.filterState.trim().split(/\s+/) : [this.filterState];
        const regexes = terms.filter(Boolean).map(term => new RegExp(_escapeRegExp(term), 'i'));
        this.models.replace(this.original.filter(model => {
          const values = this.getFilterValues(model);
          return regexes.every(regex => values.some(value => regex.test(value)));
        }));
      } else {
        this.models.replace(this.original.filter(this.filterState));
      }
    } else {
      // this is needed to get initial data in collections when filter is applied on first renders
      this.models.replace(this.get());
    }
    if (this.activePresetFilter) {
      this.models.replace(this.models.filter(this.activePresetFilter.fn));
    }
    this.discreteFilters.forEach(filter => {
      this.models.replace(this.models.filter(filter.fn));
    });
    if (guardForDuplicates) {
      const uniqueIds = [];
      const uniqueModels = this.models.filter(model => {
        const id = model.get(idPath);
        const isDuplicate = uniqueIds.includes(id);
        if (!isDuplicate) {
          uniqueIds.push(id);
          return true;
        }
        return false;
      });
      this.models.replace(uniqueModels);
    }
    this.sort();
    return this.models;
  }
  setPresetFilter(filter) {
    this.activePresetFilter = filter;
    this.filter();
  }
  resetDiscreteFilters() {
    this.discreteFilters = this.defaultDiscreteFilters;
  }
  setDiscreteFilters(discreteFilters) {
    this.discreteFilters = [...discreteFilters];
  }
  removeDiscreteFilter(type) {
    this.discreteFilters = this.discreteFilters.filter(filter => filter.type !== type);
  }
  clearFilters() {
    this.filterState = undefined;
    this.activePresetFilter = null;
    this.discreteFilters = [];
    this.models.replace(this.get());
    this.sort();
  }
  get hasFilter() {
    return !!(this.filterState || this.activePresetFilter || this.discreteFilters.length);
  }

  /**
   * Returns an array of values that filters can match against.
   * This can support matching against model properties and any other observables and computed properties.
   * To support observables and computed properties, define the property name in the collection's "filterFieldWhitelist" set.
   */
  getFilterValues(model) {
    const modelKeys = Object.keys(model.attributes);
    const whiteListKeys = this.filterFieldWhitelist && Array.from(this.filterFieldWhitelist);
    const keys = _union(modelKeys, whiteListKeys);
    return keys.filter(name => !this.filterFieldBlacklist || !this.filterFieldBlacklist.has(name)).filter(name => !this.filterFieldWhitelist || this.filterFieldWhitelist.has(name)).map(name => model.get(name) || model[name]).filter(value => value !== 'undefined');
  }
  get filterFieldBlacklist() {
    return null;
  }
  get filterFieldWhitelist() {
    return null;
  }

  /**
   * When true, a search term is split on whitespace and every word must match, in any order.
   * Defaults to false so existing collections keep matching the search term as one literal phrase.
   */
  get matchAllSearchTerms() {
    return false;
  }
  get size() {
    return this.models.length;
  }
  get selectedSize() {
    return this.selectedArray.length;
  }
  get unfilteredSize() {
    return this.get().length;
  }
  get unfiltered() {
    return this.original || this.models;
  }
  get status() {
    return this.requestStatus || 'none';
  }
  get loading() {
    return this.requestStatus === 'fetching' || this.requestStatus === 'fetchingMore' || this.requestStatus === 'awaitingRequest';
  }
  get discreteFilterValues() {
    return this.discreteFilters.reduce((acc, f) => {
      acc[f.type] = f.value;
      return acc;
    }, {});
  }
  get isAllSelected() {
    const selectableModels = this.models?.filter(model => model.isSelectable);
    return !!(this.selectedArray && this.selectedArray?.length >= selectableModels?.length &&
    /** ensure that all the selected models are the same as the models that are currently being displayed.
     * This is useful when the models are paginated, as changing the page will cause the selected.length to be the same as the selectableModels length. */
    selectableModels?.every(model => this.selectedArray?.find(selectedModel => model.get('id') === selectedModel.get('id'))));
  }
  get sortFieldIsServerSortable() {
    return !!(this.sortState.field && this.serverSortableFields.includes(this.sortState.field));
  }
  get sortFieldIsUISortLogicDriven() {
    return !!(this.sortState.field && this.uiSortLogicFields.includes(this.sortState.field));
  }
  get groupByFieldIsUISortLogicDriven() {
    return !!(this.groupBy && this.uiSortLogicFields.includes(this.groupBy));
  }
  selectAll() {
    this.selected = this.isAllSelected ? null : this.models.filter(model => model.isSelectable);
  }
  reset(options = {}) {
    this.models.clear();
    this.original = null;
    this.hasFetched = false;
    this.lastFetched = null;
    this.hasMoreResults = false;
    this.hashModelsById();
    this.requestStatus = null;
    this.fetchPromise = null;
    this.setLastUpdated();
    if (options.hard) {
      this.resetState();
    }
  }
  resetState() {
    this.hasMoreResults = false;
    this.filterState = undefined;
    this.activePresetFilter = this.defaultPresetFilter;
    this.discreteFilters = this.defaultDiscreteFilters;
    this.sortState = this.defaultSortState;
    this.groupBy = this.defaultGroupBy;
    this.filter();
  }
  resetSortState() {
    this.sortState = this.defaultSortState;
    this.sort();
  }
  resetFetchCache() {
    this.lastFetched = null;
  }
  /**
   * Can be overridden for more header handling, though note that this is entirely ignored unless
   * you pass `rawResponse` to `fetch()` calls.
   */
  processHeaders(headers) {
    if (headers['x-total-count'] != null) {
      this.setTotalCount(parseInt(headers['x-total-count']));
    }
  }
  processData(response, fetchOptions = {}) {
    if (response?.body && fetchOptions.rawResponse) {
      const {
        headers = {}
      } = response;
      this.processHeaders(headers);
      response = safelyParseJSON(response.body);
    }
    // do not clear original if we're adding more on top of it (for example loading more data via infinite scroll)
    if (!this.useAsyncAdd || !(fetchOptions?.data?.offset || fetchOptions?.data?.pagination?.offset)) {
      this.original = [];
    }
    this.hasMoreResults = response?.pagination?.has_more_results;
    const data = response ? this.deserialize(response) : [];
    if (this.useAsyncAdd || fetchOptions?.data?.offset || fetchOptions?.data?.pagination?.offset) {
      let models = [];
      return processLargeArrayAsync(data, row => {
        models = models.concat(this.build(row));
      }).then(action(() => {
        this.original = this.get().concat(models);
        this.filter(undefined, {
          idPath: fetchOptions?.idPath,
          guardForDuplicates: fetchOptions?.pagedFetch
        });
        this.setLastUpdated();
        this.hashModelsById();
        this.error = undefined;
        return response;
      }));
    }
    this.set(data);
    this.error = undefined;
    return Promise.resolve(response);
  }
  canReuseFetch(options, label) {
    return !!(!options.pagedFetch && !options.query && !options.force && (this.requestStatus === label && this.fetchPromise || this.lastFetched && this.lastFetched > Date.now() - this.minFetchInterval));
  }

  /**
   * Fetches collection data from the backend and overwrites the models, applying filter/sorts/groups.
   */
  async fetch(options = {}) {
    const {
      preserveSelection
    } = options;

    // fetching more in case we're doing a paged fetch, to update the UI somehow, indicate to the user we are loading more
    const label = options.pagedFetch ? 'fetchingMore' : 'fetching';
    if (options.pagedFetch) {
      this.useAsyncAdd = true;
    }

    // Never fetch if already fetching or if too recent - unless parameters are diff
    if (this.canReuseFetch(options, label) && _isEqual(options.data, this.lastData)) {
      return this.fetchPromise;
    }
    this.lastFetched = Date.now();
    this.requestStatus = label;
    this.hasFetched = true;
    this.fetchCount += 1;
    this.lastData = options.data;
    const count = this.fetchCount;
    if (!preserveSelection) {
      this.clearSelection();
    }
    this.fetchPromise = this.doFetch(options, label).then(response => {
      // ensure that we only display the last result if we make multiple fetches (i.e. due to options.force)
      if (this.fetchCount === count) {
        return this.processData(response, options);
      }
      // this.fetchPromise will always contain the latest attempt so we can simply chain that
      return this.fetchPromise;
    }, error => {
      if (this.fetchCount === count) {
        this.error = {
          label,
          body: error
        };
        this.models.clear();
        this.hashModelsById();
        this.fetchPromise = null;
      }
      throw error;
    }).finally(() => {
      if (this.fetchCount === count) {
        this.requestStatus = null;
      }
    });
    return this.fetchPromise;
  }
  getCacheKey(data) {
    return JSON.stringify(data);
  }
  getCachedValue(cacheOptions) {
    return this.cachedResults.get(this.getCacheKey(cacheOptions));
  }
  saveToCache(result, cacheOptions) {
    this.cachedResults.set(this.getCacheKey(cacheOptions), result);
  }

  /**
   * Just performs the actual data fetching. This method can be overridden for custom fetch mechanisms.
   */
  async doFetch(options = {}, label) {
    if (options.withCache && this.canReuseFetch(options, label)) {
      const cachedValue = this.getCachedValue(options);
      if (cachedValue) {
        return Promise.resolve(cachedValue);
      }
    }
    const {
      data,
      fetchUrl,
      fetchMethod,
      query,
      rawResponse
    } = options;
    const httpMethod = fetchMethod || this.fetchMethod;
    const url = fetchUrl || (this.urlPaths?.fetch ? this.urlPaths.fetch : this.url);
    if (!url) {
      throw new Error('Could not find URL for Collection fetch!');
    }
    return api[httpMethod](url, {
      data,
      query,
      rawResponse
    }).then(result => {
      if (options.withCache) {
        this.saveToCache(result, options);
      }
      return result;
    });
  }
  /**
   * This is very similar to fetch but allows for multiple fetch requests to go out at same time, but ONLY
   * last one will actually update the collection with results (paging, server-based collection filtering, etc.)
   */
  async queuedFetch(options = {}) {
    const {
      data,
      fetchUrl,
      fetchMethod,
      preserveSelection,
      query
    } = options;
    const label = 'fetching';
    this.requestStatus = label;
    this.hasFetched = true;
    this.useAsyncAdd = false;
    if (!preserveSelection) {
      this.clearSelection();
    }
    const key = this.queuedFetchKey;
    if (!key) {
      return Promise.reject(new Error('Collection must provide queuedFetchKey'));
    }
    const qfHttpMethod = fetchMethod || this.fetchMethod;
    const qfUrl = fetchUrl || (this.urlPaths && this.urlPaths.fetch ? this.urlPaths.fetch : this.url);
    if (!qfUrl) {
      throw new Error('Could not fetch Collection with missing qfUrl');
    }
    const {
      masterPromise,
      queue
    } = collatePromises({
      key,
      promise: api[qfHttpMethod](qfUrl, {
        data,
        query
      })
    });
    if (queue.length > 1) {
      return masterPromise;
    }
    return masterPromise.then(results => this.processData(results), error => {
      this.error = {
        label,
        body: error
      };
      this.models.clear();
      this.hashModelsById();
      throw error;
    }).finally(() => {
      this.requestStatus = null;
    });
  }
  get selectedIds() {
    const {
      selectedArray
    } = this;
    return selectedArray?.map(model => model.id);
  }
  async bulkUpdate(name, value) {
    const url = this.url || this.urlPaths?.fetch;
    const {
      selectedArray,
      selectedIds
    } = this;
    if (!selectedIds?.length || !url) {
      return null;
    }
    const data = {
      ids: selectedIds,
      name,
      value
    };
    this.setRequestStatus('updating');
    return api.post(`${url}/bulkUpdate`, {
      data
    }).then(results => {
      if (Array.isArray(results) && results[0]?.id) {
        // received an array of updated models, so update models directly
        results.forEach(result => {
          const model = this.get(result.id);
          if (model) {
            model.set(model.deserialize(result));
          }
        });
      } else {
        // didn't receive full updated data, so just update the specific field
        selectedArray.forEach(model => {
          model.set(name, value);
        });
      }
    }).finally(() => {
      this.setRequestStatus(null);
    });
  }
  async bulkDelete({
    removeFromCollection = true
  } = {}) {
    const {
      selectedArray
    } = this;
    const url = this.url || this.urlPaths?.fetch;
    if (!selectedArray?.length || !url) {
      return null;
    }
    const ids = selectedArray?.map(model => model.id);
    const data = {
      ids
    };
    this.setRequestStatus('updating');
    return api.post(`${url}/bulkDelete`, {
      data
    }).then(result => {
      if (removeFromCollection) {
        this.remove(ids);
      }
      this.clearSelection();
      this.setRequestStatus(null);
      return result;
    });
  }
  serialize(data) {
    return data || this.map(model => model.serialize());
  }
  get jsonRoot() {
    return undefined;
  }
  deserialize(data) {
    // @ts-expect-error this.jsonRoot isn't really something we can map easily
    return data && this.jsonRoot ? data[this.jsonRoot] : data;
  }
  setTotalCount(totalCount) {
    this.totalCount = totalCount;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "requestStatus", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "error", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "filterState", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "activePresetFilter", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "discreteFilters", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "sortState", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "groupBy", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "selected", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectedArray", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedArray"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "singleSelection", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "singleSelection"), _class.prototype), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "hasFetched", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "original", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "lastUpdated", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "lastFetched", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "useAsyncAdd", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "totalCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "hasMoreResults", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "fetchCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "cachedResults", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _applyDecoratedDescriptor(_class.prototype, "group", [action], Object.getOwnPropertyDescriptor(_class.prototype, "group"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "move", [action], Object.getOwnPropertyDescriptor(_class.prototype, "move"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sort", [action], Object.getOwnPropertyDescriptor(_class.prototype, "sort"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "serverSort", [action], Object.getOwnPropertyDescriptor(_class.prototype, "serverSort"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSortState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSortState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearSort", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearSort"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "filter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "filter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setPresetFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setPresetFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetDiscreteFilters", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetDiscreteFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setDiscreteFilters", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setDiscreteFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeDiscreteFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeDiscreteFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearFilters", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasFilter", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "size", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "size"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedSize", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedSize"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unfilteredSize", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "unfilteredSize"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unfiltered", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "unfiltered"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "status", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "status"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loading", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "loading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "discreteFilterValues", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "discreteFilterValues"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isAllSelected", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isAllSelected"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sortFieldIsServerSortable", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "sortFieldIsServerSortable"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sortFieldIsUISortLogicDriven", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "sortFieldIsUISortLogicDriven"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "groupByFieldIsUISortLogicDriven", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "groupByFieldIsUISortLogicDriven"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectAll"), _class.prototype), _descriptor16 = _applyDecoratedDescriptor(_class.prototype, "unselectAll", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.selected = null;
    };
  }
}), _descriptor17 = _applyDecoratedDescriptor(_class.prototype, "select", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (model, options = {}) => {
      const {
        multi = false,
        shiftKey
      } = options;
      const m = model && this.get(model.id);
      if (!m) {
        console.warn(`Model (id: ${model && model.id}) not found in ${this.constructor.name}`);
      }
      if (multi) {
        if (!this.selected || !Array.isArray(this.selected)) {
          this.selected = [];
        }
        if (model.isSelected) {
          const indexOfModel = this.selected.indexOf(model);
          if (indexOfModel >= 0) {
            this.selected = this.selected.slice(0, indexOfModel).concat(this.selected.slice(indexOfModel + 1));
          }
          if (this.selected.length === 0) {
            this.selected = null;
          }

          // adding to a multi selection
        } else {
          // select all the models in between
          if (shiftKey) {
            const firstIndex = this.selected[0].index;
            if (firstIndex > model.index) {
              this.selected = this.models.slice(model.index, firstIndex + 1).filter(item => item.isSelectable);
            } else {
              this.selected = this.models.slice(firstIndex, model.index).filter(item => item.isSelectable);
            }

            // Browsers (on some platforms) will try to select text in between the 2 cursor points. Revert that.
            document.getSelection()?.removeAllRanges();
          }
          this.selected = this.selected.concat(model);
        }
      } else {
        this.selected = model;
      }
    };
  }
}), _descriptor18 = _applyDecoratedDescriptor(_class.prototype, "clearSelection", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.selected = null;
    };
  }
}), _descriptor19 = _applyDecoratedDescriptor(_class.prototype, "setLastUpdated", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.lastUpdated = Date.now();
    };
  }
}), _descriptor20 = _applyDecoratedDescriptor(_class.prototype, "setLastFetched", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return when => {
      this.lastFetched = when || Date.now();
    };
  }
}), _descriptor21 = _applyDecoratedDescriptor(_class.prototype, "setRequestStatus", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return status => {
      this.requestStatus = status;
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetSortState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetSortState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetFetchCache", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetFetchCache"), _class.prototype), _descriptor22 = _applyDecoratedDescriptor(_class.prototype, "add", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return data => {
      if (!_isArray(data)) {
        data = [data];
      }
      const models = data.map(row => {
        if (row instanceof Model) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          row.collection = this;
          return row;
        }
        return this.build(row);
      });
      this.original = _uniqBy(this.get().concat(models), model => model.id);
      this.filter();
      this.hashModelsById();
      this.setLastUpdated();
      return models;
    };
  }
}), _descriptor23 = _applyDecoratedDescriptor(_class.prototype, "remove", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (ids, destroyModels = false) => {
      if (Array.isArray(ids)) {
        ids.forEach(id => this.remove(id, destroyModels));
      } else {
        const model = this.get(ids);
        if (model) {
          if (this.original) {
            this.original = this.original.filter(m => m !== model);
          }
          this.models.remove(model);
          if (destroyModels) {
            model.destroy();
          }
          if (Array.isArray(this.selected)) {
            const selected = this.selected.filter(selectedModel => selectedModel.id !== model.id);
            this.selected = selected.length > 0 ? selected : null;
          } else if (this.selected?.id === model.id) {
            this.clearSelection();
          }
        }
      }
      this.hashModelsById();
      this.setLastUpdated();
    };
  }
}), _descriptor24 = _applyDecoratedDescriptor(_class.prototype, "set", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return models => {
      // This is a hack to pretend to clear out the existing data and append the new models w/o triggering two observed changes to this.models
      this.original = [];
      this.add(models);
      this.setLastUpdated();
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "processHeaders", [action], Object.getOwnPropertyDescriptor(_class.prototype, "processHeaders"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "processData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "processData"), _class.prototype), _descriptor25 = _applyDecoratedDescriptor(_class.prototype, "forge", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (attributes, options = {}) => {
      const {
        select = true
      } = options;
      return attributes?.id && this.get(attributes.id) || this.build(attributes, {
        ...options,
        select
      });
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "fetch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetch"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "queuedFetch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "queuedFetch"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setTotalCount", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setTotalCount"), _class.prototype), _class);

/**
 * A concrete, non-generic version of the Collection class that can be
 * easily instantiated from JavaScript files.
 *
 * @deprecated for future code it is generally preferable to create your own type-safe Collection
 */
export default class GenericCollection extends Collection {
  // Provide a default implementation for the model getter
  get model() {
    return GenericModel;
  }
}